import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import Logger from '../../../../../../ui-kitchen/src/utils/logger';
import AmexPlatinumNightsBenefitsCard from './AmexPlatinumNightsBenefitsCard';
import AmexPlatinumNightsBenefitsCardRefresh from './AmexPlatinumNightsBenefitsCardRefresh';
import './AmexPlatinumNightsBenefits.scss';

const AmexPlatinumNightsBenefits = (
  {
    Analytics,
    awsFolderName,
    baseRequestUrl,
    benefitsConfig: {
      section_id: sectionId,
      section_title: sectionTitle,
      section_intro: sectionIntro,
      content = [],
      utm,
    },
    matchMedia,
    componentCallback,
    isPlatinumNights2026Mvp2Enabled,
  }
) => {
  const [benefitsList, setBenefitsList] = useState([]);
  const sanitizedSectionTitle = sanitizeString(sectionTitle);
  const sanitizedSectionIntro = sanitizeString(sectionIntro);

  if (!content || content.length === 0 || !sanitizedSectionTitle) {
    componentCallback('updatedHideBenefits', true);
    return false;
  }

  const validateBenefitsContent = (benefitsContent) => {
    const sharedRequiredKeys = ['title', 'copy', 'cta'];
    const requiredImageKeys = ['desktop', 'mobile'];
    const requiredCtaKeys = ['id', 'label', 'url'];
    const errMsg = ' field in AmexPlatinumNightsBenefits is null or undefined or empty. Please fix and proceed.';

    return benefitsContent.filter((item) => {
      if (!sharedRequiredKeys.every((itemKey) => !!item[itemKey] || Logger.error(itemKey + errMsg))) return false;
      if (!requiredCtaKeys.every((ctaKey) => !!item.cta[ctaKey] || Logger.error(ctaKey + errMsg))) return false;

      // Refresh path: image is not required and logo is optional.
      if (isPlatinumNights2026Mvp2Enabled) return true;

      // Legacy path: image required, logo not required
      if (!item.image || Logger.error(`image${errMsg}`)) return false;
      return requiredImageKeys.every((imageKey) => !!item.image[imageKey]?.regular || Logger.error(imageKey + errMsg));
    });
  };

  useEffect(() => {
    const filteredBenefits = validateBenefitsContent(content);
    componentCallback('updatedHideBenefits', filteredBenefits.length < 3);
    setBenefitsList(filteredBenefits);
  }, [content, isPlatinumNights2026Mvp2Enabled]);

  if (benefitsList.length < 3) return null;

  return (
    <section
      className={cx('AmexPlatinumNightsBenefits',
        { refresh: isPlatinumNights2026Mvp2Enabled })}
    >
      <div className="container">
        <h2
          id={sectionId}
          className="AmexPlatinumNightsBenefits__title"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: sanitizedSectionTitle,
          }}
        />
        {sanitizedSectionIntro && (
          <p
            className="AmexPlatinumNightsBenefits__intro"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedSectionIntro,
            }}
          />
        )}
        <ul className="AmexPlatinumNightsBenefits__benefits-grid">
          {benefitsList.map((benefitsItem, index) => (
            <li key={benefitsItem?.title || index} className="AmexPlatinumNightsBenefits__benefit-item">
              {isPlatinumNights2026Mvp2Enabled ? (
                <AmexPlatinumNightsBenefitsCardRefresh
                  Analytics={Analytics}
                  awsFolderName={awsFolderName}
                  baseRequestUrl={baseRequestUrl}
                  matchMedia={matchMedia}
                  cardConfig={benefitsItem}
                  utm={utm}
                  index={index}
                  componentCallback={componentCallback}
                />
              ) : (
                <AmexPlatinumNightsBenefitsCard
                  Analytics={Analytics}
                  awsFolderName={awsFolderName}
                  baseRequestUrl={baseRequestUrl}
                  matchMedia={matchMedia}
                  cardConfig={benefitsItem}
                  utm={utm}
                  index={index}
                  componentCallback={componentCallback}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default AmexPlatinumNightsBenefits;

AmexPlatinumNightsBenefits.propTypes = {
  Analytics: PropTypes.shape({}).isRequired,
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  benefitsConfig: PropTypes.shape({
    section_id: PropTypes.string,
    section_title: PropTypes.string,
    section_intro: PropTypes.string,
    content: PropTypes.arrayOf(PropTypes.shape({})),
    utm: PropTypes.string,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  componentCallback: PropTypes.func.isRequired,
  isPlatinumNights2026Mvp2Enabled: PropTypes.bool.isRequired,
};
